import * as Ladda from 'ladda';
import NavigationUtils from './NavigationUtils';
import StringUtils from './StringUtils';
import { MaterialType, PopupMode } from '../enums';
import { flattenAgencyMediaUrls, queueSourceUsage, resolveAgencyMediaDropPack, stampPendingAgencyMediaPack } from './agencyMediaTransfer';
import {
	bindSourcesPickerDetachButton,
	isSourcesArchivePicker,
	onSourcesPickerOffcanvasClosed,
	startSourcesPickerHost,
} from './sourcesPickerHost';

export default class ResourceUtils {
	static #offcanvasResourcePickerOpenCountKey = '__naxosOffcanvasResourcePickerOpenCount';
	static #globalDropzoneInterceptorReady = false;
	static #currentGlobalDropZone = null;

	static #incrementOffcanvasResourcePicker() {
		if (typeof window === 'undefined') return;
		window[ResourceUtils.#offcanvasResourcePickerOpenCountKey] = (window[ResourceUtils.#offcanvasResourcePickerOpenCountKey] ?? 0) + 1;
	}

	static #decrementOffcanvasResourcePicker() {
		if (typeof window === 'undefined') return;
		const current = window[ResourceUtils.#offcanvasResourcePickerOpenCountKey] ?? 0;
		window[ResourceUtils.#offcanvasResourcePickerOpenCountKey] = Math.max(0, current - 1);
	}

	static #isOffcanvasResourcePickerOpen() {
		if (typeof window === 'undefined') return false;
		return (window[ResourceUtils.#offcanvasResourcePickerOpenCountKey] ?? 0) > 0;
	}

	static #shouldCloseOffcanvasAfterDrop($dropZone) {
		if (!ResourceUtils.#isOffcanvasResourcePickerOpen()) return false;

		// If the drop happened inside an editor gallery component, keep the offcanvas open
		// so the user can keep dropping multiple images.
		return !$dropZone.closest('.gallery-tool').length;
	}

	/**
	 * 
	 * @param {number} size 
	 * @returns {string}
	 */
	static getReadableFileSize(size) {
		if (size < 1024) {
			return size + 'B';
		}
		if (size < 1024 * 1024) {
			return (size / 1024).toFixed(2) + 'KB';
		}
		if (size < 1024 * 1024 * 1024) {
			return (size / 1024 / 1024).toFixed(2) + 'MB';
		}
		return (size / 1024 / 1024 / 1024).toFixed(2) + 'GB';
	}

	/**
	 * Attach drag and drop events using custom jQuery events (dropzone-drop, etc.)
	 * These events are triggered by EditorJS's drag interceptor.
	 *
	 * Supports two signatures:
	 * 1. Legacy: attachDragEvents(selector, elementType, dropCallback, uploadCallback, inputElementId, maxFiles)
	 *    - dropCallback(url, poster, caption, author) - called at the end with the result
	 *    - uploadCallback(file) - called to process dropped files
	 *    - inputElementId - string ID of a hidden input element
	 *
	 * 2. Modern (when 5th param is a function): attachDragEvents(element, type, onDragStart, onDrop, onDragEnd, maxFiles)
	 *    - onDragStart(e) - called when drag starts (optional, can be null)
	 *    - onDrop(urlOrFile) - called to process dropped file or URL, should return URL
	 *    - onDragEnd(url) - called at the end with the final URL
	 */
	static attachDragEvents(selector, elementType, dropCallback = null, uploadCallback = null, inputElementId = null, maxFiles = null) {
		if (!selector) return;

		if ($('body').hasClass('popup-translation')) return;

		// Detect which signature is being used
		const isModernSignature = typeof inputElementId === 'function';

		// Modern signature: (element, type, onDragStart, onDrop, onDragEnd, maxFiles)
		let onDragStart = null;
		let onDrop = null;
		let onDragEnd = null;

		if (isModernSignature) {
			onDragStart = dropCallback; // 3rd param becomes onDragStart
			onDrop = uploadCallback; // 4th param becomes onDrop
			onDragEnd = inputElementId; // 5th param becomes onDragEnd
		}

		if (typeof selector === 'string' || selector instanceof Element) {
			selector = $(selector);
		}
		if (!selector.length) return;
		if (!selector.hasClass('drop-zone')) {
			selector.addClass('drop-zone');

			selector.append(`<i class="fa-regular fa-hand-back-fist drag-picker-icon"></i>`);
		}

		selector
			.on('dropzone-dragover', function (e) {
				if (!$(e.currentTarget).is(selector)) {
					return;
				}

				e.preventDefault();
				e.stopPropagation();

				if (!$(this).hasClass('drag-over')) {
					$(this).addClass('drag-over');
				}
			})
			.on('dropzone-dragenter', function (e) {
				if (!$(e.currentTarget).is(selector)) {
					return;
				}

				e.preventDefault();
				e.stopPropagation();

				if (!$(this).hasClass('drag-over')) {
					$(this).addClass('drag-over');
				}

				if (isModernSignature && onDragStart) {
					onDragStart(e);
				}
			})
			.on('dropzone-dragleave', function (e) {
				if (!$(e.currentTarget).is(selector)) {
					return;
				}

				e.preventDefault();
				e.stopPropagation();

				// Controlla se il mouse è ancora nell'area
				if (!$(this).has('drag-over').length) {
					$(this).removeClass('drag-over');
				}
			})
			.on('dropzone-drop', async (e) => {
				if (!$(e.currentTarget).is(selector)) {
					return;
				}

				const originalEvent = e.detail?.originalEvent;
				if (originalEvent?.__naxosDropzoneDropConsumed) {
					return;
				}

				if (originalEvent) {
					originalEvent.__naxosDropzoneDropConsumed = true;
				}

				e.preventDefault();
				e.stopPropagation();

				$(e.currentTarget).removeClass('drag-over');

				const dataTransfer = e.detail?.dataTransfer || e.dataTransfer || null;
				if (!dataTransfer) return;

				const items = dataTransfer.items;
				const files = dataTransfer.files;

				const agencyPack = resolveAgencyMediaDropPack(dataTransfer);
				if (agencyPack?.blocks?.length) {
					stampPendingAgencyMediaPack(agencyPack);

					let urls = flattenAgencyMediaUrls(agencyPack.blocks);
					if (maxFiles != null && maxFiles > 0) {
						urls = urls.slice(0, maxFiles);
					}

					if (isModernSignature) {
						let resultUrl = null;

						for (const url of urls) {
							if (!url || !onDrop) {
								continue;
							}

							resultUrl = await onDrop(url);
							// Gallery needs each URL applied via onDragEnd (Sources.URL).
							if (onDragEnd && resultUrl && (maxFiles == null || maxFiles !== 1)) {
								onDragEnd(resultUrl);
							}
						}

						if (onDragEnd && resultUrl && maxFiles === 1) {
							onDragEnd(resultUrl);
						}

						if (resultUrl && agencyPack.identifier) {
							queueSourceUsage(agencyPack.source || 'agency', agencyPack.identifier);
						}

						if (resultUrl && ResourceUtils.#shouldCloseOffcanvasAfterDrop($(e.currentTarget))) {
							document.dispatchEvent(new CustomEvent('closeOffcanvas'));
						}

						return;
					}

					// Legacy signature (cover / older dropzones): upload first URL and forward metadata.
					const firstUrl = urls[0] || null;
					if (!firstUrl) {
						document.dispatchEvent(new CustomEvent('naxos-dropzone-rejected'));
						window.toastr.warning('Formato non valido o non compatibile');
						return;
					}

					const imageBlock = agencyPack.blocks.find((block) => block?.type === 'image');
					const caption =
						imageBlock?.data?.caption ||
						imageBlock?.data?.file?.caption ||
						null;
					const author =
						imageBlock?.data?.author ||
						imageBlock?.data?.file?.author ||
						agencyPack.signature ||
						null;

					let uploaded = firstUrl;
					if (uploadCallback) {
						uploaded = await uploadCallback(firstUrl);
					}

					if (uploaded && dropCallback) {
						dropCallback(uploaded, null, caption, author);

						if (agencyPack.identifier) {
							queueSourceUsage(agencyPack.source || 'agency', agencyPack.identifier);
						}

						if (ResourceUtils.#shouldCloseOffcanvasAfterDrop($(e.currentTarget))) {
							document.dispatchEvent(new CustomEvent('closeOffcanvas'));
						}
					} else if (!uploaded) {
						document.dispatchEvent(new CustomEvent('naxos-dropzone-rejected'));
						window.toastr.warning('Formato non valido o non compatibile');
					}

					return;
				}

				if (isModernSignature) {
					// Modern signature: call onDrop with file or URL
					let resultUrl = null;

					if (files.length > 0) {
						// Handle dropped files
						let validFiles = Array.from(files).filter((file) =>
							elementType === MaterialType.VIDEO ? this.isVideoUrl(file.name) : this.isImageUrl(file.name),
						);

						if (maxFiles != null && maxFiles > 0) {
							validFiles = validFiles.slice(0, maxFiles);
						}

						for (const file of validFiles) {
							if (onDrop) {
								resultUrl = await onDrop(file);
							}
						}
					} else if (items.length > 0 || elementType === MaterialType.VIDEO) {
						// Cross-iframe video drags often expose items.length === 0 on the drop target.
						const url = await this.#extractUrlFromItems(items, dataTransfer, elementType);
						if (url && onDrop) {
							resultUrl = await onDrop(url);
						}
					}

					// Call onDragEnd with the result
					if (onDragEnd && resultUrl) {
						onDragEnd(resultUrl);
					}

					if (resultUrl && ResourceUtils.#shouldCloseOffcanvasAfterDrop($(e.currentTarget))) {
						document.dispatchEvent(new CustomEvent('closeOffcanvas'));
					}
				} else {
					// Legacy signature
					let url = null;
					let poster = null;
					let caption = null;
					let author = null;

					if (files.length > 0) {
						url = await this.#handleDroppedFiles(files, elementType, inputElementId, uploadCallback, maxFiles);
					} else if (items.length > 0 || elementType === MaterialType.VIDEO) {
						const dragPayload =
							elementType === MaterialType.VIDEO ? this.#peekNaxosDragPayload() : null;
						url = await this.#handleDroppedItems(items, dataTransfer, elementType, uploadCallback, maxFiles);
						if (elementType === MaterialType.VIDEO) {
							poster =
								dragPayload?.posterUrl ||
								(await this.#handleDroppedItems(items, dataTransfer, MaterialType.PHOTO, uploadCallback, maxFiles));
							if (url) {
								const consumed = this.#consumeNaxosDragPayload();
								queueSourceUsage(consumed?.source || dragPayload?.source, consumed?.identifier || dragPayload?.identifier);
							}
						}
					} else {
						return false;
					}

					if (url && dropCallback) {
						dropCallback(url, poster, caption, author);

						if (ResourceUtils.#shouldCloseOffcanvasAfterDrop($(e.currentTarget))) {
							document.dispatchEvent(new CustomEvent('closeOffcanvas'));
						}
					} else if (!url || (Array.isArray(url) && url.length === 0)) {
						document.dispatchEvent(new CustomEvent('naxos-dropzone-rejected'));
						window.toastr.warning('Formato non valido o non compatibile');
					}
				}
			});
	}

	/**
	 * Extract URL from dropped items (HTML, plain text, URI list)
	 */
	static async #extractUrlFromItems(items, dataTransfer, elementType) {
		if (elementType === MaterialType.VIDEO) {
			return this.#getDroppedVideoUrl(dataTransfer);
		}

		const htmlData = dataTransfer.getData('text/html');
		const plainData = dataTransfer.getData('text/plain');
		const uriData = dataTransfer.getData('text/uri-list');

		if (htmlData) {
			const fromHtml = this.#extractUrlFromHtml(htmlData, elementType);
			if (fromHtml) {
				return fromHtml;
			}
		}

		if (plainData && this.isImageUrl(plainData)) {
			return plainData;
		}

		if (uriData && this.isImageUrl(uriData)) {
			return uriData;
		}

		return null;
	}

	static async #handleDroppedFiles(files, elementType, inputElementId = null, uploadCallback = null, maxFiles = null) {
		files = Array.from(files).filter((file) => (elementType === MaterialType.VIDEO ? this.isVideoUrl(file.name) : this.isImageUrl(file.name)));

		if (maxFiles && files.length > maxFiles) {
			files = files.slice(0, maxFiles);
		}

		if (files.length) {
			if (inputElementId) {
				const fileInput = document.getElementById(inputElementId);
				if (fileInput && files.length > 0) {
					const dataTransfer = new DataTransfer();
					files.forEach((file) => dataTransfer.items.add(file));
					fileInput.files = dataTransfer.files;
					fileInput.dispatchEvent(new Event('change'));
				}
				if (uploadCallback) {
					uploadCallback(maxFiles === 1 ? files[0] : files);
				}
			} else {
				files = await Promise.all(files.map((file) => uploadCallback(file)));
			}
		}

		if (files.length === 0) {
			return Promise.resolve(maxFiles === 1 ? null : []);
		}

		return Promise.resolve(maxFiles === 1 ? files[0] : files);
	}

	static async #handleDroppedItems(items, dataTransfer, elementType, uploadCallback = null, maxFiles = null) {
		if (elementType === MaterialType.VIDEO) {
			const videoUrl = this.#getDroppedVideoUrl(dataTransfer);
			if (!videoUrl) {
				return Promise.resolve(null);
			}

			const result = uploadCallback ? await uploadCallback(videoUrl) : videoUrl;
			return Promise.resolve(result);
		}

		const seenUrls = new Set();
		let accumulator = [];

		const addUrl = (url) => {
			if (!url || seenUrls.has(url)) {
				return;
			}

			seenUrls.add(url);

			if (uploadCallback) {
				accumulator.push(uploadCallback(url));
			} else {
				accumulator.push(url);
			}
		};

		for (let i = 0; i < items.length; i++) {
			let url = null;
			if (items[i].type.startsWith('image/')) {
				const rawData = dataTransfer.getData('text/html') || dataTransfer.getData('text/plain');
				if (rawData?.startsWith('<')) {
					url = this.#extractUrlFromHtml(rawData, elementType);
				} else if (rawData?.trim() && this.isImageUrl(rawData.trim())) {
					url = rawData.trim();
				}
			}

			if (items[i].type === 'text/plain') {
				const extractedUrl = dataTransfer.getData('text/plain')?.trim();
				if (extractedUrl && this.isImageUrl(extractedUrl)) {
					url = extractedUrl;
				}
			}

			if (items[i].type === 'text/html') {
				const html = dataTransfer.getData('text/html');
				const extractedFromHtml = this.#extractUrlFromHtml(html, elementType);
				if (extractedFromHtml) {
					url = extractedFromHtml;
				}
			}

			if (!url) {
				const uriData = dataTransfer.getData('text/uri-list')?.trim();
				if (uriData && this.isImageUrl(uriData)) {
					url = uriData;
				}
			}

			if (url) {
				addUrl(url);
			}

			if (maxFiles && accumulator.length >= maxFiles) {
				break;
			}
		}

		if (accumulator.length === 0) {
			return Promise.resolve(null);
		}
		if (uploadCallback) {
			accumulator = await Promise.all(accumulator);
		}

		return Promise.resolve(maxFiles === 1 ? accumulator[0] : accumulator);
	}

	static #dragPayloadWindows() {
		const windows = [window];

		try {
			if (window.parent !== window) {
				windows.push(window.parent);
			}
			if (window.top !== window && !windows.includes(window.top)) {
				windows.push(window.top);
			}
		} catch {
			// Cross-origin parent/top access is not available.
		}

		return windows;
	}

	static #peekNaxosDragPayload() {
		for (const payload_window of this.#dragPayloadWindows()) {
			const payload = payload_window.__naxosDragPayload;
			if (!payload?.videoUrl?.trim()) {
				continue;
			}

			if (Date.now() - (payload.at ?? 0) > 60000) {
				delete payload_window.__naxosDragPayload;
				continue;
			}

			return {
				videoUrl: payload.videoUrl.trim(),
				posterUrl: payload.posterUrl?.trim() || null,
				source: payload.source ?? null,
				identifier: payload.identifier ?? null,
			};
		}

		return null;
	}

	static #consumeNaxosDragPayload() {
		const payload = this.#peekNaxosDragPayload();
		if (!payload) {
			return null;
		}

		for (const payload_window of this.#dragPayloadWindows()) {
			if (payload_window.__naxosDragPayload?.videoUrl?.trim()) {
				delete payload_window.__naxosDragPayload;
			}
		}

		return payload;
	}

	static peekVideoDragPayload() {
		return this.#peekNaxosDragPayload();
	}

	static consumeVideoDragPayload() {
		return this.#consumeNaxosDragPayload();
	}

	static getDroppedVideoUrlFromTransfer(dataTransfer) {
		if (!dataTransfer) {
			return null;
		}

		return this.#getDroppedVideoUrl(dataTransfer);
	}

	static shouldCloseOffcanvasAfterDrop(dropZone) {
		return this.#shouldCloseOffcanvasAfterDrop($(dropZone));
	}

	static isValidVideoResourceUrl(url) {
		return this.#isDroppedVideoResourceUrl(url);
	}

	static isAppPageUrl(url) {
		return this.#isAppPageUrl(url);
	}

	static storeNaxosDragPayload(targetWindow, videoUrl, posterUrl = null, usage = null) {
		if (!targetWindow || !videoUrl?.trim() || this.#isAppPageUrl(videoUrl)) {
			return;
		}

		targetWindow.__naxosDragPayload = {
			videoUrl: videoUrl.trim(),
			posterUrl: posterUrl?.trim() || null,
			source: usage?.source ?? null,
			identifier: usage?.identifier ?? null,
			at: Date.now(),
		};
	}

	/** @param HTMLElement|null|undefined videoLink */
	static resolveVideoDragUrls(videoLink) {
		if (!videoLink) {
			return { videoUrl: null, posterUrl: null };
		}

		const card = videoLink.closest('[data-naxos-video-url], .card-wrapper, .card, .panel.card, [data-id]');
		const copyVideoBtn = card?.querySelector('[data-action="copy-video-url"][data-copy]');
		const copyImageBtn = card?.querySelector('[data-action="copy-image-url"][data-copy]');

		const videoUrl =
			card?.getAttribute('data-naxos-video-url')?.trim() ||
			videoLink.getAttribute('data-video-url')?.trim() ||
			videoLink.getAttribute('video-href')?.trim() ||
			(videoLink.classList.contains('popup-preview-video') ? videoLink.getAttribute('href')?.trim() : null) ||
			copyVideoBtn?.getAttribute('data-copy')?.trim() ||
			null;

		const posterUrl =
			card?.getAttribute('data-naxos-poster-url')?.trim() ||
			videoLink.getAttribute('data-naxos-poster-url')?.trim() ||
			videoLink.querySelector('img')?.getAttribute('src')?.trim() ||
			copyImageBtn?.getAttribute('data-copy-thumb')?.trim() ||
			copyImageBtn?.getAttribute('data-copy')?.trim() ||
			null;

		return { videoUrl, posterUrl };
	}

	static resolveVideoDragUrlsFromElement(element) {
		if (!element) {
			return { videoUrl: null, posterUrl: null };
		}

		const videoLink = element.closest?.(
			'a.popup-preview-video, a.popup-preview-image[video-href], a.popup-preview-image[data-video-url]',
		);
		if (videoLink) {
			return this.resolveVideoDragUrls(videoLink);
		}

		const card = element.closest?.('[data-naxos-video-url]');
		if (!card) {
			return { videoUrl: null, posterUrl: null };
		}

		return {
			videoUrl: card.getAttribute('data-naxos-video-url')?.trim() || null,
			posterUrl: card.getAttribute('data-naxos-poster-url')?.trim() || null,
		};
	}

	static initGlobalDropzoneInterceptor() {
		if (this.#globalDropzoneInterceptorReady || typeof document === 'undefined') {
			return;
		}

		this.#globalDropzoneInterceptorReady = true;

		const interceptDragEvents = (e) => {
			const targetElement = e.target;
			const dropZoneElement =
				targetElement?.classList?.contains('dropzone-container') && targetElement?.classList?.contains('drop-zone')
					? targetElement
					: targetElement?.closest?.('.dropzone-container.drop-zone');

			if (!dropZoneElement) {
				return;
			}

			e.preventDefault();
			e.stopPropagation();

			if (e.type === 'drop') {
				if (e.naxosDropZoneHandled) {
					return false;
				}

				e.naxosDropZoneHandled = true;
				e.stopImmediatePropagation();
			}

			if (e.type === 'dragenter') {
				if (this.#currentGlobalDropZone !== dropZoneElement) {
					this.#currentGlobalDropZone = dropZoneElement;
					dropZoneElement.dispatchEvent(
						new CustomEvent('dropzone-dragenter', {
							detail: {
								dataTransfer: e.dataTransfer,
								relatedTarget: e.relatedTarget,
								originalEvent: e,
								dropZoneElement,
							},
							bubbles: true,
							cancelable: true,
						}),
					);
				}
			} else if (e.type === 'dragleave') {
				if (e.relatedTarget && !dropZoneElement.contains(e.relatedTarget)) {
					this.#currentGlobalDropZone = null;
					dropZoneElement.dispatchEvent(
						new CustomEvent('dropzone-dragleave', {
							detail: {
								dataTransfer: e.dataTransfer,
								relatedTarget: e.relatedTarget,
								originalEvent: e,
								dropZoneElement,
							},
							bubbles: true,
							cancelable: true,
						}),
					);
				}
			} else {
				dropZoneElement.dispatchEvent(
					new CustomEvent(`dropzone-${e.type}`, {
						detail: {
							dataTransfer: e.dataTransfer,
							relatedTarget: e.relatedTarget,
							originalEvent: e,
							dropZoneElement,
						},
						bubbles: true,
						cancelable: true,
					}),
				);

				if (e.type === 'drop') {
					this.#currentGlobalDropZone = null;
				}
			}

			return false;
		};

		['dragover', 'dragenter', 'dragleave', 'drop'].forEach((eventType) => {
			document.addEventListener(eventType, interceptDragEvents, true);
		});
	}

	static #isAppPageUrl(url) {
		if (!url?.trim() || !/^https?:\/\//i.test(url)) {
			return false;
		}

		try {
			const parsed = new URL(url, window.location.origin);
			if (parsed.origin !== window.location.origin) {
				return false;
			}

			return /\/app\//i.test(parsed.pathname);
		} catch {
			return false;
		}
	}

	static #isEmbeddableVideoUrl(url) {
		if (!url?.trim()) {
			return false;
		}

        const providers = ['youtube', 'vimeo', 'facebookVideo', 'twitchVideo', 'twitchChannel', 'meride', 'streamup'];
		for (const provider of providers) {
			const { code, type } = StringUtils.extractVideoCode(url, provider);
			if (code && type === provider) {
				return true;
			}
		}

		return false;
	}

	static #isDroppedVideoResourceUrl(url) {
		if (!url?.trim() || this.#isAppPageUrl(url)) {
			return false;
		}

		return this.isVideoUrl(url) || this.#isEmbeddableVideoUrl(url);
	}

	static #isLikelyDroppedVideoUrl(url) {
		if (!url?.trim() || this.#isAppPageUrl(url) || this.isImageUrl(url)) {
			return false;
		}

		if (this.#isDroppedVideoResourceUrl(url)) {
			return true;
		}

		const trimmed = url.trim();

		return /^https?:\/\//i.test(trimmed) || (!trimmed.includes(' ') && trimmed.length > 0);
	}

	static #getDroppedVideoUrl(dataTransfer) {
		const payload = this.#peekNaxosDragPayload();
		const customUrl = dataTransfer?.getData('application/x-naxos-video-url')?.trim();
		const html = dataTransfer?.getData('text/html');
		const plain = dataTransfer?.getData('text/plain')?.trim();
		const uri = dataTransfer?.getData('text/uri-list')?.trim();

		const resolved =
			(payload?.videoUrl && !this.#isAppPageUrl(payload.videoUrl) ? payload.videoUrl : null) ||
			(customUrl && !this.#isAppPageUrl(customUrl) ? customUrl : null) ||
			(html ? this.#extractUrlFromHtml(html, MaterialType.VIDEO) : null) ||
			[uri, plain].find((c) => this.#isLikelyDroppedVideoUrl(c)) ||
			null;

		return resolved;
	}

	/**
	 *
	 * @param {string} html
	 * @param {string} elementType video or image
	 * @returns
	 */
	static #extractUrlFromHtml(html, elementType) {
		// Crea un elemento temporaneo per parsare l'HTML
		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = html;

		if (elementType === MaterialType.VIDEO) {
			const naxosVideoHost = tempDiv.querySelector('[data-naxos-video-url]');
			const naxosVideoUrl = naxosVideoHost?.getAttribute('data-naxos-video-url')?.trim();
			if (naxosVideoUrl && !this.#isAppPageUrl(naxosVideoUrl)) {
				return naxosVideoUrl;
			}

			const previewVideoLink = tempDiv.querySelector('a.popup-preview-video, a.popup-preview-image[video-href], a[data-video-url]');
			const previewHref =
				previewVideoLink?.getAttribute('data-video-url')?.trim() ||
				previewVideoLink?.getAttribute('video-href')?.trim() ||
				previewVideoLink?.getAttribute('href')?.trim();
			if (previewHref && !this.#isAppPageUrl(previewHref)) {
				return previewHref;
			}

			const links = tempDiv.querySelectorAll('a');
			for (const link of links) {
				const videoHref = link.getAttribute('video-href')?.trim() || link.getAttribute('data-video-url')?.trim();
				if (videoHref && !this.#isAppPageUrl(videoHref)) {
					return videoHref;
				}
			}

			const videoElement = tempDiv.querySelector('video');
			const videoSrc = videoElement?.getAttribute('src')?.trim();
			if (videoSrc && this.#isDroppedVideoResourceUrl(videoSrc)) {
				return videoSrc;
			}

			for (const link of links) {
				const href = link.getAttribute('href')?.trim();
				if (href && this.isVideoUrl(href)) {
					return href;
				}
			}

			return null;
		}

		// Cerca tag
		const element = tempDiv.querySelector('img');
		let lowres = null;
		// _LOWRES. sono URL di immagini di bassa qualità, quindi non vengono considerati
		if (element && element.src) {
			if (!element.src.includes('_LOWRES.')) {
				if (this.isImageUrl(element.src)) {
					return element.src;
				}
			} else if (this.isImageUrl(element.src)) {
				lowres = element.src;
			}
		}

		// Cerca link che potrebbero essere immagini
		const links = tempDiv.querySelectorAll('a');
		for (const link of links) {
			if (link.href && this.isImageUrl(link.href)) {
				return link.href;
			}
		}

		// lascio un fallback per le immagini se non ho trovato niente di meglio della bassa qualità
		return lowres;
	}

	/**
	 * Verifica se un URL punta a un'immagine
	 * @param {string} url - URL da verificare
	 * @returns {boolean}
	 */
	static isVideoUrl(url) {
		return this.#matchExtensions(url, ['.mp4', '.mov', '.mpg']);
	}

	/**
	 * Verifica se un URL punta a un'immagine
	 * @param {string} url - URL da verificare
	 * @returns {boolean}
	 */
	static isImageUrl(url) {
		return this.#matchExtensions(url, ['.jpg', '.jpeg', '.png', /*'.gif',*/ '.webp', /*'.svg',*/ '.bmp']);
	}

	static #matchExtensions(url, extensions) {
		const lowerUrl = url.toLowerCase();
		return extensions.some((ext) => lowerUrl.includes(ext));
	}

	static #getPopupContent(popupUrl, hideFields, showPreview, inputFieldLabel, secondaryUrlWithMessage, materialType, mode, customMessage = null, displayConfirmButton = true) {
		let content = '';
		if (popupUrl) {
			popupUrl = NavigationUtils.appendPopupQueryParams(popupUrl, {
				offcanvas: mode === PopupMode.OFFCANVAS,
			});
			const iframe_class = mode === PopupMode.OFFCANVAS ? 'border bg-grey-100 offcanvas-iframe' : 'border bg-grey-100';
			content = `<div class="row popup-iframe-container mt-${mode === PopupMode.OFFCANVAS ? '0' : '30'}">
				<div class="col-md-12 d-flex">
					<div class=" d-flex flex-one">
						<iframe src="${popupUrl}" width="100%" height="100%" class="${iframe_class}"></iframe>
					</div>
				</div>
			</div>`;
		}

		content += `<div class="row popup-selectors-container mt-${!popupUrl ? '30' : '10'}">
			<div class="col-md-12 d-flex">
				<div class="${hideFields ? 'hidden' : 'd-flex flex-column flex-one mr-10'}">
					<h6 class="m-0 font-size-12"><label for="resource-url">${inputFieldLabel}</label></h6>
					<div class="${showPreview ? 'border rounded h-full' : ''}">
						<input id="resource-url" type="${materialType === MaterialType.VIDEO ? 'url' : 'text'}" class="form-control ${showPreview ? 'border-0' : ''}" name="url" value="" style="${
							popupUrl ? 'border: 2px solid var(--primary);' : ''
						}">
					</div>`;
		if (secondaryUrlWithMessage) {
			content += `<h6 class="font-size-12 mt-1 mb-0"><label for="resource-secondary-url">${secondaryUrlWithMessage}</label></h6>
					<input id="resource-secondary-url" type="${materialType === MaterialType.VIDEO ? 'url' : 'text'}" class="form-control" name="secondary-url" value="">`;
		}
		content += `</div>`;

		if (showPreview) {
			content += `<div class="resource-preview-container mr-10">
				<img class="img-fluid resource-preview-url" src="" alt="" style="object-fit: contain; max-width: 200px; max-height: 100px;">
			</div>`;
		}

		content += `<div class="${hideFields ? 'w-full text-center' : 'd-flex'}">
					${displayConfirmButton ? `<button type="button" id="resource-apply-url" class="btn btn-primary ladda-button w-full" data-style="zoom-in">
						<i class="fa-solid fa-check mr-1"></i>
						<span class="ladda-label">${customMessage || 'Conferma'}</span>
					</button>` : `<span class="customMessage">${customMessage}</span>`}
				</div>
			</div>
		</div>`;

		return content;
	}

	static #closePopupWithError(mode, message = 'URL inserito non valido') {
		window.toastr.error(message);
		if (mode === PopupMode.POPUP) {
			ResourceUtils.#closePopup(mode);
		}
	}

	static #closePopup(mode) {
		if (mode === PopupMode.POPUP) {
			$.magnificPopup.close();
		} else if (mode === PopupMode.OFFCANVAS) {
			document.dispatchEvent(new CustomEvent('closeOffcanvas'));
		}
	}

	static #validatePopupFields($urlField, $secondaryUrlField, materialType, secondaryUrlWithMessage, mode) {
		if (materialType !== MaterialType.TEXT && !ResourceUtils.validURL($urlField.val())) {
			ResourceUtils.#closePopupWithError(mode, $urlField.val() === '' ? 'Selezionare un URL' : 'URL inserito non valido');
			return false;
		}

		if (
			materialType !== MaterialType.TEXT &&
			secondaryUrlWithMessage &&
			$secondaryUrlField.val() !== '' &&
			!ResourceUtils.validURL($secondaryUrlField.val())
		) {
			ResourceUtils.#closePopupWithError(mode);
			return false;
		}

		return true;
	}

	static #fillPopup(
		popupUrl,
		inputFieldLabel,
		customUploadFunction,
		secondaryUrlWithMessage,
		hideFields,
		showPreview,
		containerSelector,
		resolveCallback,
		uploadEndpoint = null,
		mode = PopupMode.POPUP,
		materialType = null,
		modalId = null, // Unique identifier for this modal instance
		onUploadProgress = null,
		customMessage = null,
		displayConfirmButton = true,
	) {
		const urlSelector = '#resource-url';
		const secondaryUrlSelector = '#resource-secondary-url';
		const applySelector = '#resource-apply-url';

		// Generate unique modal ID if not provided
		const instanceId = modalId || `modal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

		$(containerSelector).empty();

		const content = this.#getPopupContent(popupUrl, hideFields, showPreview, inputFieldLabel, secondaryUrlWithMessage, materialType, mode, customMessage, displayConfirmButton);

		$(containerSelector).html(content);

		setTimeout(function () {
			const $urlField = $(urlSelector);
			const $applyButton = $(applySelector);
			let timeout = null;
			let lastResolvedUrl = null; // Track last confirmed URL

			$urlField.on('keyup', function (e) {
				if (e.key === 'Enter' || e.keyCode === 13) {
					$applyButton.trigger('click');
				} else {
					clearTimeout(timeout);
					timeout = setTimeout(function () {
						const $preview = $('.resource-preview-url');
						const newUrl = $urlField.val();
						if ($preview.length && newUrl.length && newUrl.startsWith('http')) {
							$preview.attr('src', NavigationUtils.getProxyStreamUrl(newUrl));
						} else {
							$preview.attr('src', '');
						}
					}, 300);
				}
			});

			$applyButton.off('click').on('click', async function (e) {
				if (!ResourceUtils.#validatePopupFields($urlField, $(secondaryUrlSelector), materialType, secondaryUrlWithMessage, mode)) {
					return false;
				}

				const currentUrl = $urlField.val();

				// Allow confirmation even if URL is the same - user may have re-selected an item
				// Just ensure we always process the current selection

				let aiLaddaBtn = Ladda.create(document.querySelector(applySelector));
				aiLaddaBtn.start();

				try {
					let data;
					if (uploadEndpoint || customUploadFunction) {
						data = customUploadFunction ? await customUploadFunction(currentUrl) : await ResourceUtils.upload(currentUrl, uploadEndpoint, onUploadProgress);
					} else {
						data = { url: currentUrl, secondaryUrl: $(secondaryUrlSelector).val() };
					}

					// Always update last resolved data
					lastResolvedUrl = currentUrl;

					// Call resolve callback (this will only work once for a Promise)
					// Include modalId in the resolved data
					resolveCallback({ ...data, _modalId: instanceId });

					// Also trigger a custom event that can be listened to multiple times
					const confirmEvent = new CustomEvent('resourceConfirmed', {
						detail: { data, url: currentUrl, modalId: instanceId },
					});
					document.dispatchEvent(confirmEvent);

					ResourceUtils.#closePopup(mode);
				} catch (err) {
					console.error(err);
					window.toastr?.error('Errore durante la conferma della risorsa');
				} finally {
					aiLaddaBtn.stop();
				}
			});

			// Remove any existing message listeners to prevent duplicates
			const messageHandler = function (event) {
				if (typeof event.data !== 'object' || !event.data?.action) return;
				if (event.data.action === 'set-url' && event.data.url) {
					const resourceUrl = document.getElementById('resource-url');
					if (resourceUrl) {
						resourceUrl.value = event.data.url;
						// Reset last resolved to allow re-confirmation
						lastResolvedUrl = null;
						// Trigger preview update
						const $preview = $('.resource-preview-url');
						const previewUrl = event.data.thumb || event.data.url;
						if ($preview.length && previewUrl) {
							$preview.attr('src', NavigationUtils.getProxyStreamUrl(previewUrl));
						}
					}

					if (event.data.source && event.data.identifier) {
						window.__pendingSourceUsage = {
							source: event.data.source,
							identifier: String(event.data.identifier),
						};
					}
				}
				if (event.data.action === 'set-secondary-url' && event.data.url) {
					const secondaryUrl = document.getElementById('resource-secondary-url');
					if (secondaryUrl) {
						secondaryUrl.value = event.data.url;
					}
				}
			};

			// Remove previous handler if it exists to prevent memory leaks
			if (window._resourcePopupMessageHandler) {
				window.removeEventListener('message', window._resourcePopupMessageHandler);
			}
			window._resourcePopupMessageHandler = messageHandler;
			window.addEventListener('message', messageHandler);
		}, 100);
	}

	/**
	 * @param {string|null} popupUrl
	 * @param {string|null} uploadEndpoint
	 * @param {string} inputFieldLabel
	 * @param {Function|null} customUploadFunction
	 * @param {boolean} secondaryUrlWithMessage
	 * @param {boolean} hideFields
	 * @param {boolean} showPreview
	 * @param {string} mode
	 *
	 * @return {Promise<{data: string|null, url: string|null, caption: string, author: string|null}>}
	 */
	static selectResourceByUrl(
		popupUrl = null,
		uploadEndpoint = null,
		inputFieldLabel = "Incolla l'URL della risorsa",
		customUploadFunction = null,
		secondaryUrlWithMessage = false,
		hideFields = false,
		showPreview = true,
		mode = PopupMode.POPUP,
		title = null,
		materialType = null,
		onUploadProgress = null,
		customMessage = null,
		displayConfirmButton = true,
	) {
		// Generate unique modal ID for this instance
		const modalId = `modal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
		const cancelledResponse = { data: null, url: null, caption: '', author: null, source_url: null };

		return new Promise(async function (resolve, _reject) {
			let isResolved = false;
			let didTrackOffcanvas = false;
			const safeResolve = (data) => {
				if (isResolved) {
					return;
				}
				isResolved = true;
				resolve(data);
			};

			if (mode === PopupMode.OFFCANVAS) {
				ResourceUtils.#incrementOffcanvasResourcePicker();
				didTrackOffcanvas = true;
				const sourcesPickerSessionId =
					isSourcesArchivePicker(popupUrl) && typeof crypto !== 'undefined' && crypto.randomUUID
						? crypto.randomUUID()
						: null;

				if (sourcesPickerSessionId) {
					bindSourcesPickerDetachButton();
					startSourcesPickerHost({
						sessionId: sourcesPickerSessionId,
						popupUrl: String(popupUrl),
					});
				}

				const fillPopup = () => {
					ResourceUtils.#fillPopup(
						popupUrl,
						inputFieldLabel,
						customUploadFunction,
						secondaryUrlWithMessage,
						hideFields,
						showPreview,
						'#offcanvas-content',
						safeResolve,
						uploadEndpoint,
						mode,
						materialType,
						modalId,
						onUploadProgress,
						customMessage,
						displayConfirmButton,
					);
				};

				document.addEventListener('offcanvasopened', fillPopup);
				document.addEventListener('offcanvasclosed', () => {
					document.removeEventListener('offcanvasopened', fillPopup);
					if (didTrackOffcanvas) {
						ResourceUtils.#decrementOffcanvasResourcePicker();
						didTrackOffcanvas = false;
					}
					if (sourcesPickerSessionId) {
						onSourcesPickerOffcanvasClosed();
					}
					safeResolve(cancelledResponse);
				});

				setTimeout(() => {
					NavigationUtils.openUrlInOffcanvas(popupUrl, title);
				}, 100);
			} else if (mode === PopupMode.POPUP || !mode) {
				$.magnificPopup.open({
					items: {
						src: $('#popup'),
					},
					type: 'inline',
					midClick: true,
					closeOnBgClick: false,
					callbacks: {
						open: function () {
							ResourceUtils.#fillPopup(
								popupUrl,
								inputFieldLabel,
								customUploadFunction,
								secondaryUrlWithMessage,
								hideFields,
								showPreview,
								'#popup-content',
								safeResolve,
								uploadEndpoint,
								mode,
								materialType,
								modalId,
								onUploadProgress,
								customMessage,
								displayConfirmButton,
							);
						},
						close: function () {
							$('#popup-content').empty();
							safeResolve(cancelledResponse);
						},
					},
				});
			}
		});
	}

	/**
	 *
	 * @param {Blob} files
	 * @param {string|null} uploadEndpoint
	 * @return {Promise<{data: string|null, url: string|null, caption: string, author: string|null}>}
	 */
	static async uploadNewFileFromReader(files, uploadEndpoint = null, resourceName = 'resource', source = 'locale', onUploadProgress = null) {
		try {
			if (!files.length) {
				return Promise.resolve({ data: null, caption: '' });
			}

			function load(file) {
				return new Promise(function (resolve, _reject) {
					const data = { caption: '', author: null, data: null, url: null, source, source_url: null };
					const fileReader = new FileReader();
					fileReader.readAsDataURL(file);
					fileReader.onload = function () {
						data.data = this.result;
						file.src = data.data;
					};

					fileReader.onloadend = function () {
						if (uploadEndpoint) {
							resolve(data);
							return;
						}

						ResourceUtils.extractMetaTags(file)
							.then((tags) => {
								data.author = tags.Copyright || tags.Author || tags.Artist || tags.Creator || tags.creator || tags.AuthorByLine || null;
								const description = tags.Caption || tags.caption || tags.description || tags.ImageDescription || tags.Description || null;
								if (description && description !== '') data.caption += (data.caption.length ? '. ' : '') + description;
							})
							.catch((e) => console.error(e))
							.finally(() => resolve(data));
					};
				});
			}

			const file = files[0];

			// if (!/^image\/\w+$/.test(file.type)) {
			// 	throw new Error('Only images are accepted');
			// }

			if (uploadEndpoint) {
				return Promise.all([ResourceUtils.upload(file, uploadEndpoint, resourceName, onUploadProgress), load(file)]).then((resp) => ({ ...resp[0], data: resp[1].data }));
			}

			return load(file);
		} catch (e) {
			Promise.reject(e);
		}
	}

	/**
	 * Verify if is valid URL
	 * @param str
	 * @returns {boolean}
	 */
	static validURL(str) {
		try {
			new URL(str);
			return true;
		} catch (e) {
			return false;
		}
		// let pattern = new RegExp(
		// 	'^(https?:\\/\\/)?' + // protocol
		// 		'((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
		// 		'((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
		// 		'(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
		// 		'(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
		// 		'(\\#[-a-z\\d_]*)?$',
		// 	'i',
		// ); // fragment locator
		// return !!pattern.test(str);
	}

	/**
	 *
	 * @param {File|string} file
	 * @param {string} uploadEndopint
	 * @param {Function|null} onUploadProgress
	 */
	static async upload(file, uploadEndpoint, resourceName = 'resource', onUploadProgress = null) {
		const formData = new FormData();
		formData.append(resourceName, file);

		return new Promise(function (resolve, reject) {
			$.ajax({
				url: uploadEndpoint,
				method: 'POST',
				data: formData,
				processData: false,
				contentType: false,
				xhr: function () {
					const xhr = $.ajaxSettings.xhr();
					if (onUploadProgress && xhr.upload) {
						xhr.upload.addEventListener('progress', function (e) {
							if (!e.lengthComputable) {
								onUploadProgress({ loaded: e.loaded, total: null, percent: null });
								return;
							}
							onUploadProgress({
								loaded: e.loaded,
								total: e.total,
								percent: Math.round((e.loaded / e.total) * 10000) / 100,
							});
						});
					}
					return xhr;
				},
				success: function (data) {
					resolve({ 
						url: data.file.url, 
						thumb: data.file.thumb || null, 
						caption: data.file.caption, 
						author: data.file.author 
					});
				},
				error: function (jqXHR, textStatus, errorThrown) {
					reject(textStatus);
				},
			});
		});
	}

	/**
	 *
	 * @param {string|Blob|ArrayBuffer} file
	 * @returns Promise<object>
	 */
	static extractMetaTags(file) {
		return new Promise(function (resolve, reject) {
			if (!window.imagesReadIptcs) {
				resolve({});
				return;
			}

			EXIF.enableXmp();
			EXIF.getData(file, function () {
				const iptcs = EXIF.getAllTags(this);
				if (this.xmpdata) {
					Object.assign(iptcs, ResourceUtils.#parseXmps(this.xmpdata));
				}
				resolve(iptcs);
			});
		});
	}

	static #parseXmps(tags) {
		const xmps = {};
		if (tags['x:xmpmeta'] && tags['x:xmpmeta']['rdf:RDF'] && tags['x:xmpmeta']['rdf:RDF']['rdf:Description']) {
			const result = tags['x:xmpmeta']['rdf:RDF']['rdf:Description'];
			for (let key in result) {
				if (key === '@attributes') continue;
				ResourceUtils.#cleanXmpStructure(key, result[key], xmps);
			}
		}

		return xmps;
	}

	/**
	 * Attach native DOM drag and drop events to an element for handling file/URL drops.
	 * Use this for elements OUTSIDE EditorJS (e.g., standalone dropzones).
	 * For EditorJS components, use attachDragEvents() which listens for custom dropzone events.
	 * @param {HTMLElement} element - The target element for drag events
	 * @param {string} materialType - The type of material (PHOTO, VIDEO, etc.)
	 * @param {Function|null} onDragStart - Callback for drag start (optional)
	 * @param {Function} onDrop - Callback when a file/URL is dropped
	 * @param {Function|null} onDragEnd - Callback for drag end (optional)
	 * @param {number} maxFiles - Maximum number of files to accept (default: 1)
	 */
	static attachNativeDragEvents(element, materialType, onDragStart = null, onDrop, onDragEnd = null, maxFiles = 1) {
		if (!element || !onDrop) return;

		const imageExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.bmp', '.gif'];
		const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];

		const isValidUrl = (url) => {
			try {
				new URL(url);
				return true;
			} catch {
				return false;
			}
		};

		const isImageUrl = (url) => {
			const lowerUrl = url.toLowerCase();
			return imageExtensions.some((ext) => lowerUrl.includes(ext));
		};

		const isVideoUrl = (url) => {
			const lowerUrl = url.toLowerCase();
			return videoExtensions.some((ext) => lowerUrl.includes(ext));
		};

		const extractUrlFromHtml = (html) => {
			const tempDiv = document.createElement('div');
			tempDiv.innerHTML = html;
			const img = tempDiv.querySelector('img');
			if (img && img.src) return img.src;
			const links = tempDiv.querySelectorAll('a');
			for (const link of links) {
				if (link.href && (isImageUrl(link.href) || isVideoUrl(link.href))) {
					return link.href;
				}
			}
			return null;
		};

		element.addEventListener('dragover', (e) => {
			e.preventDefault();
			e.stopPropagation();
			element.classList.add('drag-over');
		});

		element.addEventListener('dragenter', (e) => {
			e.preventDefault();
			e.stopPropagation();
			element.classList.add('drag-over');
			if (onDragStart) onDragStart(e);
		});

		element.addEventListener('dragleave', (e) => {
			e.preventDefault();
			e.stopPropagation();
			if (!element.contains(e.relatedTarget)) {
				element.classList.remove('drag-over');
				if (onDragEnd) onDragEnd(e);
			}
		});

		element.addEventListener('drop', async (e) => {
			e.preventDefault();
			e.stopPropagation();
			element.classList.remove('drag-over');

			const dataTransfer = e.dataTransfer;
			const files = dataTransfer.files;
			const items = dataTransfer.items;

			let resultUrl = null;

			// Handle dropped files
			if (files && files.length > 0) {
				const validFiles = Array.from(files).slice(0, maxFiles);
				for (const file of validFiles) {
					if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
						resultUrl = await onDrop(file);
					}
				}
				if (onDragEnd && resultUrl) onDragEnd(resultUrl);
				return;
			}

			// Handle dropped URLs/HTML
			if (items && items.length > 0) {
				let url = null;

				// Try to get URL from different formats
				const htmlData = dataTransfer.getData('text/html');
				const plainData = dataTransfer.getData('text/plain');
				const uriData = dataTransfer.getData('text/uri-list');

				if (uriData && isValidUrl(uriData)) {
					url = uriData;
				} else if (htmlData) {
					url = extractUrlFromHtml(htmlData);
				} else if (plainData && isValidUrl(plainData)) {
					url = plainData;
				}

				if (url) {
					resultUrl = await onDrop(url);
					if (onDragEnd) onDragEnd(resultUrl || url);
				}
			}
		});
	}

	/**
	 * @param {string} key
	 * @param {array|string} tag
	 * @param {object} xmps
	 */
	static #cleanXmpStructure(key, tag, xmps, prevKey = null, depth = 0, visited = new WeakSet()) {
		// Evita troppa ricorsione
		const MAX_DEPTH = 100;
		if (depth > MAX_DEPTH) {
			console.warn('Recursion depth exceeded');
			return;
		}

		// Evita cicli
		if (typeof tag === 'object') {
			if (visited.has(tag)) {
				console.warn('Cycle detected');
				return;
			}
			visited.add(tag);
		}

		if (typeof tag === 'object' && !Array.isArray(tag) && '@attributes' in tag) {
			delete tag['@attributes'];
		}

		const properties = Object.keys(tag);
		if (properties.length === 1 && properties.includes('#text')) {
			let name = key !== 'rdf:li' ? key.slice(key.indexOf(':') + 1) : prevKey.slice(prevKey.indexOf(':') + 1);
			xmps[name] = tag['#text'];
		} else {
			for (let attribute in tag) {
				if (Array.isArray(tag[attribute])) continue;
				ResourceUtils.#cleanXmpStructure(attribute, tag[attribute], xmps, prevKey || (!/\d+/.test(key) ? key : null), depth + 1, visited);
			}
		}
	}
}

export class ImageUtils extends ResourceUtils {
	/**
	 * @param {Function} converter
	 * @param {string|null} popupUrl
	 * @param {string|null} fileUrl
	 * @param {string|null} uploadEndpoint
	 * @param {string} inputFieldLabel
	 * @param {boolean} secondaryUrlWithMessage
	 * @param {boolean} hideFields
	 *
	 * @return {Promise<{data: string|null, url: string|null, caption: string, author: string|null}>}
	 */
	static uploadNewFileFromUrl(
		converter,
		popupUrl = null,
		fileUrl = null,
		uploadEndpoint = null,
		inputFieldLabel = "Incolla l'URL dell'immagine",
		secondaryUrlWithMessage = false,
		hideFields = false,
		showPreview = true,
		source = null,
		onUploadProgress = null,
	) {
		/** @param {string} url */
		async function upload(url) {
			const defaultResponse = {
				url: null,
				data: null,
				caption: '',
				author: null,
				source,
				source_url: null,
			};

			if (uploadEndpoint) {
				const data = await ResourceUtils.upload(url, uploadEndpoint, 'image', onUploadProgress);
				data.source_url = typeof url === 'string' ? url : null;
				if (!data.url) {
					return defaultResponse;
				}

				const converted = await converter(data.url);
				if (typeof converted !== 'string' && !(converted instanceof Blob)) {
					return defaultResponse;
				}
				data.data = converted;
				return data;
			} else if (fileUrl) {
				const tags = await ResourceUtils.extractMetaTags(fileUrl);
				author = tags.Copyright || tags.Author || tags.Artist || tags.creator || null;
				caption = tags.caption || tags.description || tags.ImageDescription || tags.Description || '';
				const converted = await converter(url);
				if (typeof converted !== 'string') {
					return defaultResponse;
				}
				return {
					url: null,
					data: converted,
					caption,
					author,
					source,
					source_url: fileUrl,
				};
			}

			const converted = await converter(url);
			if (typeof converted !== 'string') {
				return defaultResponse;
			}

			return {
				url: null,
				data: converted,
				caption: '',
				author: null,
				source,
				source_url: url,
			};
		}

		return new Promise(async function (resolve, _reject) {
			if (fileUrl && uploadEndpoint) {
				const data = await upload(fileUrl);
				resolve(data);
			} else {
				resolve(
					await ResourceUtils.selectResourceByUrl(
						popupUrl,
						uploadEndpoint,
						inputFieldLabel,
						upload,
						secondaryUrlWithMessage,
						hideFields,
						showPreview,
						onUploadProgress,
					),
				);
			}
		});
	}

	/**
	 *
	 * @param {Blob} files
	 * @param {string|null} uploadEndpoint
	 * @return {Promise<{data: string|null, url: string|null, caption: string, author: string|null}>}
	 */
	static async uploadNewFileFromReader(files, uploadEndpoint = null, source = 'locale', onUploadProgress = null) {
		try {
			if (!files.length) {
				return Promise.resolve({ data: null, caption: '' });
			}

			const file = files[0];

			if (!/^image\/\w+$/.test(file.type)) {
				throw new Error('Only images are accepted');
			}

			return ResourceUtils.uploadNewFileFromReader(files, uploadEndpoint, 'image', source, onUploadProgress);
		} catch (e) {
			Promise.reject(e);
		}
	}

	/**
	 * Transform URL to Base64
	 * @param src
	 * @param callback
	 * @param outputFormat
	 * @return Promise<string>
	 */
	static toDataURL(src, outputFormat) {
		if (!src) return Promise.reject(new Error('URL non valido'));

		return new Promise(function (resolve, reject) {
			const image = new Image();
			image.crossOrigin = 'anonymous';
			image.onload = function (e) {
				const canvas = document.createElement('canvas');
				const ctx = canvas.getContext('2d');
				canvas.height = this.naturalHeight;
				canvas.width = this.naturalWidth;
				ctx.drawImage(this, 0, 0);
				const dataURL = canvas.toDataURL(outputFormat);
				resolve(dataURL);
			};
			image.src = src;
			if (image.complete || image.complete === undefined) {
				image.src = 'data:image/gif;base64, R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
				image.src = src;
			}
		});
	}

	/**
	 * Transform URL to Image Object
	 * @param {*} src
	 * @param {*} callback
	 * @return Promise<File>
	 */
	static toBlobObject(src) {
		if (!src) return Promise.reject(new Error('URL non valido'));

		return new Promise(function (resolve, reject) {
			fetch(NavigationUtils.getProxyStreamUrl(src), {
				method: 'GET',
			})
				.then((res) => res.blob())
				.then((blob) => {
					const filename = src.split('/').pop();
					const file = new File([blob], filename, { type: blob.type });
					resolve(file);
				})
				.catch((error) => reject(error));
		});
	}

	handleDroppedFiles(files, inputElementId, maxFiles = null) {
		const allowedTypes = ['image/jpeg', 'image/png', /*'image/gif',*/ 'image/webp', /*'image/svg+xml',*/ 'image/bmp'];
		let imageFiles = Array.from(files).filter((file) => allowedTypes.includes(file.type));

		if (maxFiles && imageFiles.length > maxFiles) {
			imageFiles = imageFiles.slice(0, maxFiles);
		}

		const fileInput = document.getElementById(inputElementId);
		if (fileInput && imageFiles.length > 0) {
			const dataTransfer = new DataTransfer();
			imageFiles.forEach((file) => dataTransfer.items.add(file));
			fileInput.files = dataTransfer.files;
			fileInput.dispatchEvent(new Event('change'));
		}
	}

	handleDroppedItems(items, dataTransfer, maxFiles = null, uploadCallback = null, onUploadProgress = null) {
		const uploadPromises = [];
		for (let i = 0; i < items.length; i++) {
			if (items[i].type.startsWith('image/')) {
				const url = dataTransfer.getData('text/html') || dataTransfer.getData('text/plain');
				if (url) {
					uploadPromises.push(uploadCallback ? uploadCallback(url) : this.uploadFromUrl(null, null, url, onUploadProgress));
				}
			} else if (items[i].type === 'text/plain') {
				const url = dataTransfer.getData('text/plain');
				if (url && this.isImageUrl(url)) {
					uploadPromises.push(uploadCallback ? uploadCallback(url) : this.uploadFromUrl(null, null, url, onUploadProgress));
				}
			} else if (items[i].type === 'text/html') {
				const html = dataTransfer.getData('text/html');
				const imageUrl = this.extractImageUrlFromHtml(html);
				if (imageUrl) {
					uploadPromises.push(uploadCallback ? uploadCallback(imageUrl) : this.uploadFromUrl(null, null, imageUrl, onUploadProgress));
				}
			}
			if (maxFiles && uploadPromises.length >= maxFiles) {
				break;
			}
		}

		return Promise.all(uploadPromises);
	}

	/**
	 * Verifica se un URL punta a un'immagine
	 * @param {string} url - URL da verificare
	 * @returns {boolean}
	 */
	isImageUrl(url) {
		const imageExtensions = ['.jpg', '.jpeg', '.png', /*'.gif',*/ '.webp', /*'.svg',*/ '.bmp'];
		const lowerUrl = url.toLowerCase();

		// Controlla estensioni di file
		if (imageExtensions.some((ext) => lowerUrl.includes(ext))) {
			return true;
		}

		// Controlla se contiene parole chiave comuni per immagini
		// const imageKeywords = ['image', 'img', 'photo', 'picture'];
		// if (imageKeywords.some((keyword) => lowerUrl.includes(keyword))) {
		// 	return true;
		// }

		return false;
	}

	/**
	 * Estrae l'URL di un'immagine da HTML
	 * @param {string} html - HTML da analizzare
	 * @returns {string|null} URL dell'immagine o null
	 */
	extractImageUrlFromHtml(html) {
		// Crea un elemento temporaneo per parsare l'HTML
		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = html;

		// Cerca tag img
		const img = tempDiv.querySelector('img');
		if (img && img.src) {
			return img.src;
		}

		// Cerca link che potrebbero essere immagini
		const links = tempDiv.querySelectorAll('a');
		for (const link of links) {
			if (link.href && this.isImageUrl(link.href)) {
				return link.href;
			}
		}

		return null;
	}
}

export class CropUtils extends ImageUtils {
	#crops = {};
	#container_prefix = null;
	#cropUpdated = false;
	#cropEnabled = false;
	#init = false;
	#enableAutoCrop = false;
	#tracker = {};
	#isRestoring = false;

	// FIXME: non sto mai attivando l'auto inquadramento delle immagini per motivi di performance e malfunzionamenti
	constructor(cropConfigs, modelCropsData = {}, checkRoute = null, container_prefix = null, enableAutoCrop = false, onUploadProgress = null) {
		super();
		this.#container_prefix = container_prefix;
		this.#initCrops(cropConfigs, modelCropsData);
		this.#initEvents(onUploadProgress);
		this.#enableAutoCrop = enableAutoCrop;

		if (checkRoute) {
			this.#checkCropsExistence(checkRoute);
		}
	}

	get crops() {
		return this.#crops;
	}

	get cropUpdated() {
		return this.#cropUpdated;
	}

	get cropEnabled() {
		return this.#cropEnabled;
	}

	#hasValidCropData(data) {
		if (!data || typeof data !== 'object') return false;
		const n = (v) => typeof v === 'number' && Number.isFinite(v);
		return n(data.x) && n(data.y) && n(data.width) && n(data.height);
	}

	#restoreCropData(cropperInstance, savedData) {
		if (!cropperInstance || !this.#hasValidCropData(savedData)) return;

		this.#isRestoring = true;

		try {
			const hasCanvasState = typeof savedData.canvasRatio === 'number' && savedData.canvasRatio > 0;
			if (!hasCanvasState) {
				cropperInstance.setData(savedData);
				return;
			}

			const containerData = cropperInstance.getContainerData();
			const imageData = cropperInstance.getImageData();
			if (containerData.width <= 0 || imageData.naturalWidth <= 0) {
				cropperInstance.setData(savedData);
				return;
			}

			const targetWidth = containerData.width * savedData.canvasRatio;
			const zoomRatio = targetWidth / imageData.naturalWidth;
			cropperInstance.zoomTo(zoomRatio);

			const n = (v) => typeof v === 'number' && Number.isFinite(v);
			if (n(savedData.canvasLeftRatio) || n(savedData.canvasTopRatio)) {
				const currentCanvas = cropperInstance.getCanvasData();
				cropperInstance.setCanvasData({
					left: n(savedData.canvasLeftRatio) ? savedData.canvasLeftRatio * containerData.width : currentCanvas.left,
					top: n(savedData.canvasTopRatio) ? savedData.canvasTopRatio * containerData.height : currentCanvas.top,
				});
			}

			cropperInstance.setData(savedData);
		} finally {
			this.#isRestoring = false;
		}
	}

	#initCrops(cropConfigs, modelCropsData) {
		for (const key in cropConfigs) {
			this.#crops[key] = {};
			this.#crops[key].cropper = document.querySelector(this.#container_prefix || `#cropper_${key} img`);
			if (this.#crops[key].cropper && !this.#crops[key].cropper.crossOrigin) {
				this.#crops[key].cropper.crossOrigin = 'anonymous';
			}
			this.#crops[key].ratio = cropConfigs[key].ratio;
			this.#crops[key].width = cropConfigs[key].width;
			this.#crops[key].height = cropConfigs[key].height;
			this.#crops[key].data = typeof modelCropsData !== 'undefined' && modelCropsData[key] !== undefined ? modelCropsData[key] : null;
			this.#crops[key].cropper.addEventListener('cropend', () => {
				if (this.#isRestoring) return;
				this.#cropUpdated = true;
				this.confirmCrop();
				this.#hideBroken(key);
			});
			this.#crops[key].cropper.addEventListener('zoom', () => {
				if (this.#isRestoring) return;
				this.#cropUpdated = true;
				this.confirmCrop();
			});
			// let loaderTimeout = setTimeout(() => {
			$(`.crop-loader-${key}`).show();
			// }, 200);
			const savedData = this.#crops[key].data;
			const hasCustomZoom = savedData && typeof savedData.canvasRatio === 'number' && savedData.canvasRatio > 0;
			this.#crops[key].crop = new Cropper(this.#crops[key].cropper, {
				responsive: true,
				viewMode: 0,
				aspectRatio: this.#crops[key].ratio,
				zoomOnWheel: false,
				zoomOnTouch: false,
				autoCropArea: 1,
				checkCrossOrigin: true,
				data: hasCustomZoom ? null : savedData,
				ready: () => {
					// clearTimeout(loaderTimeout);
					this.#restoreCropData(this.#crops[key].crop, this.#crops[key].data);
					$(`.crop-loader-${key}`).hide();
					if (this.#init) {
						this.#cropEnabled = true;
						this.#cropUpdated = true;
						this.confirmCrop();
					}
				},
				cropstart: () => {
					if (this.#enableAutoCrop) this.stopTracking(key);
				},
			});
		}
		this.#init = true;
	}

	#initEvents(onUploadProgress = null) {
		this.#cropEnabled = true;
		const crops = this.#crops;
		$('.crop-action').on('click', (e) => {
			const target = e.currentTarget;
			let input = null;
			const data = {
				method: target.getAttribute('data-method'),
				target: target.getAttribute('data-target'),
				option: target.getAttribute('data-option') || undefined,
				option2: target.getAttribute('data-second-option') || undefined,
			};

			const cropper = crops[data.target]?.crop;

			if (!data.method || !cropper || !cropper[data.method]) return;

			const result = data.option2 ? cropper[data.method](data.option, data.option2) : cropper[data.method](data.option);

			switch (data.method) {
				case 'scaleX':
				case 'scaleY':
					target.setAttribute('data-option', -data.option);
					break;
			}

			if (typeof result === 'object' && result !== cropper && input) {
				try {
					input.value = JSON.stringify(result);
				} catch (e) {
					console.log(e.message);
				}
			}

			this.#cropUpdated = true;
			this.confirmCrop();
		});

		$('[data-reset]').on('click', () => this.resetCrops());
		$('[data-delete]').on('click', () => this.deleteCrops());

		let isChangingEnabledCrops = false;
		let reEnableTimeout = null;
		const updateLock = () => {
			if (reEnableTimeout) {
				clearTimeout(reEnableTimeout);
				reEnableTimeout = null;
			}

			reEnableTimeout = setTimeout(() => {
				this.#toggleCropsLock(true);
				reEnableTimeout = null;
			}, 300);
		};

		window.addEventListener(
			'resize',
			() => {
				if (this.#cropEnabled && !isChangingEnabledCrops) {
					isChangingEnabledCrops = true;
					this.#toggleCropsLock(false);
					isChangingEnabledCrops = false;
				}

				updateLock();
			},
			true,
		);

		const $dropZone = $('.crop-editor .drop-zone');

		// Previeni il comportamento di default del browser
		$(document).on('dragover drop', function (e) {
			e.preventDefault();
		});

		$dropZone.on('dragover', function (e) {
			e.preventDefault();
			e.stopPropagation();
			$(this).addClass('drag-over');
		});

		$dropZone.on('dragenter', function (e) {
			e.preventDefault();
			e.stopPropagation();
			$(this).addClass('drag-over');
		});

		$dropZone.on('dragleave', function (e) {
			e.preventDefault();
			e.stopPropagation();

			// Controlla se il mouse è ancora nell'area
			if (!$(this).has(e.relatedTarget).length) {
				$(this).removeClass('drag-over');
			}
		});

		$dropZone.on('drop', async (e) => {
			e.preventDefault();
			e.stopPropagation();

			$(e.currentTarget).removeClass('drag-over');

			const files = e.originalEvent.dataTransfer.files;
			const items = e.originalEvent.dataTransfer.items;

			if (files.length > 0) {
				this.handleDroppedFiles(files, 'inputImage', 1);
			} else if (items.length > 0) {
				//TODO: a questo punto non so che converter usare e che uploadurl usare
				const uploadedFiles = await this.handleDroppedItems(items, e.originalEvent.dataTransfer, 1, (url) => this.uploadFromUrl(
					null, 
					null, 
					url,
					null,
					false,
					false,
					null,
					onUploadProgress
				));
				console.log(uploadedFiles.shift());
			}
		});
	}

	#signCropAsBroken(el) {
		const $parent = $(el).parent();
		const $next = $parent.next();
		$next.find('.fa-triangle-exclamation').show();
		$next.find('.fa-spinner').hide();

		console.warn('Crop', $parent.parent().attr('data-crop'), 'mancante');
	}

	#signCropAsValid(el) {
		$(el).parent().next().find('.fa-triangle-exclamation').hide();
	}

	/**
	 *
	 * @param {string} checkRoute
	 */
	#checkCropsExistence(checkRoute) {
		if ($('#crop_original').val() && checkRoute) {
			$('.cropper img[data-crop]').each((_idx, el) => {
				const url = $(el).attr('data-crop');
				if (url) {
					$.ajax({
						url: checkRoute + '?url=' + url,
						type: 'GET',
						success: (data) => {
							if (!data.exists) {
								this.#signCropAsBroken(el);
							} else {
								this.#signCropAsValid(el);
							}
						},
						error: (_data) => this.#signCropAsBroken(el),
					});
				}
			});
		}
	}

	#isTrackingAvailable() {
		return typeof window.tracking !== 'undefined' && typeof window.tracking.ObjectTracker === 'function';
	}

	setupAutoCrop(key) {
		if (typeof window.tracking === 'undefined') {
			this.#enableAutoCrop = false;
			console.warn('Tracking.js non disponibile, auto-crop disabilitato');
			return;
		}

		try {
			const cropper = this.#crops[key].crop;

			if (this.#tracker[key]) {
				this.#tracker[key].removeAllListeners();
			}

			this.#tracker[key] = new window.tracking.ObjectTracker('face');
			this.#tracker[key].setEdgesDensity(0.1);
			this.#tracker[key].setInitialScale(4);
			this.#tracker[key].setStepSize(2);

			this.#tracker[key].on('track', (event) => {
				console.log('Track event:', event); // Debug
				if (event.data.length > 0) {
					// console.log('Volti rilevati:', event.data.length); // Debug

					const face = event.data.reduce((prev, current) => (prev.width * prev.height > current.width * current.height ? prev : current));

					// console.log('Volto selezionato:', face); // Debug

					const currentCropBox = cropper.getCropBoxData();
					const faceCenter = {
						x: face.x + face.width / 2,
						y: face.y + face.height / 2,
					};

					// console.log('Centro del volto:', faceCenter); // Debug

					const newPosition = {
						left: Math.max(0, faceCenter.x - currentCropBox.width / 2),
						top: Math.max(0, faceCenter.y - currentCropBox.height / 2),
					};

					const imageData = cropper.getImageData();
					newPosition.left = Math.min(newPosition.left, imageData.width - currentCropBox.width);
					newPosition.top = Math.min(newPosition.top, imageData.height - currentCropBox.height);

					// console.log('Nuova posizione:', newPosition); // Debug

					cropper.setCropBoxData({
						...currentCropBox,
						left: newPosition.left,
						top: newPosition.top,
					});
				} else {
					console.log('Nessun volto rilevato'); // Debug
				}
			});

			this.#tracker[key].on('error', (error) => {
				console.error('Tracking error:', error);
			});

			const imageElement = cropper.element;
			console.log('Avvio tracking su elemento:', imageElement); // Debug

			// Assicuriamoci che l'immagine sia completamente caricata
			if (imageElement.complete) {
				window.tracking.track(imageElement, this.#tracker[key]);
			} else {
				imageElement.onload = () => {
					window.tracking.track(imageElement, this.#tracker[key]);
				};
			}

			console.debug('Face tracking inizializzato');
		} catch (error) {
			console.error("Errore durante l'inizializzazione del face tracking:", error);
			console.error('Stack:', error.stack); // Debug completo dell'errore
		}
	}

	stopTracking(key) {
		if (this.#tracker[key]) {
			this.#tracker[key].removeAllListeners();
			this.#tracker[key] = null;
		}
	}

	#showLoaders() {
		for (const key in this.#crops) {
			this.#showLoader(key);
		}
	}

	#showLoader(loader) {
		console.log('showLoader', loader);
		$(`.crop-uploader-${loader}`).show();
		$(`.crop-loader-${loader}`).hide();
	}

	#hideLoaders() {
		for (const key in this.#crops) {
			this.#hideLoader(key);
		}
	}

	#hideLoader(loader) {
		console.log('hideLoader', loader);
		$(`.crop-uploader-${loader}`).hide();
		$(`.crop-loader-${loader}`).hide();
	}

	#hideBrokens() {
		for (const key in this.#crops) {
			this.#hideBroken(key);
		}
	}

	#hideBroken(loader) {
		$(`.crop-broken-${loader}`).hide();
	}

	confirmCrop() {
		if (!this.cropUpdated || this.#isRestoring) return;

		$('#crop-editor-placeholders-container').hide();
		$('#crop-editor-placeholders-label').hide();
		$('#crop-editor-crops-container').show();
		$('.only-if-original').show();

		const croppedCanvas = this.getCroppedCanvas();
		let isChanged = false;
		for (const key in croppedCanvas) {
			const oldValue = $('#crop_' + key).val();
			const newValue = JSON.stringify(croppedCanvas[key]);
			$('#crop_' + key).val(newValue);
			if (oldValue !== newValue && !isChanged) isChanged = true;
			this.#hideLoader(key);
		}

		if (isChanged) {
			$('#crop_updated').val('true');
			$('#pageForm').trigger('change');
		}
	}

	/**
	 *
	 * @param {boolean} enable
	 */
	#toggleCropsLock(enable) {
		for (const key in this.#crops) {
			this.#crops[key].crop[enable ? 'enable' : 'disable']();
		}
		this.#cropEnabled = enable;
	}

	#setUploaded(data) {
		if (data?.data) {
			this.#cropUpdated = true;
			this.#hideBrokens();
		}
		if (data?.url) {
			$('#crop_original').val(data.url);
			const preview_src = NavigationUtils.getProxyStreamUrl(data.url);
			const $preview = $('.crop-editor .popup-preview-image');
			$preview.removeAttr('crossorigin');
			if (/^https?:\/\//i.test(data.url)) {
				$preview.attr('crossOrigin', 'anonymous');
			}
			$preview.attr('src', preview_src);
			$('.crop-editor [data-copy]').attr('data-copy', data.url);
			$('.crop-editor [data-download]').attr('data-download', data.url);
		}
		const source = data.source?.replace(/http(?:s)?:\/\//, '').replace(/\/(?:.*)/, '') ?? '';
		const source_url = data.source_url ?? '';
		$('#crop_source').val(source);
		$('#crop_source_url').val(source_url);
		$('#source-display').html(`<i class="fa-solid fa-link mr-1 fa-fw"></i> ${source}`);
	}

	async uploadFromUrl(converter, popupUrl = null, fileUrl = null, uploadEndpoint = null, secondaryUrlWithMessage = false, hideFields = false, source = null, onUploadProgress = null) {
		try {
			this.#showLoaders();
			const data = await ImageUtils.uploadNewFileFromUrl(
				converter,
				popupUrl,
				fileUrl,
				uploadEndpoint,
				undefined,
				secondaryUrlWithMessage,
				hideFields,
				source,
				onUploadProgress,
			);
			if (!data?.data && !data?.url) {
				this.#hideLoaders();
				return { caption: '', author: null, data: null, url: null, source, source_url: null };
			}
			this.#setUploaded(data);
			return data;
		} catch (e) {
			console.error(e.message);
			this.#hideLoaders();
		}
	}
	async uploadFromReader(files, uploadEndpoint = null, source = 'locale', onUploadProgress = null) {
		try {
			this.#showLoaders();
			const data = await ImageUtils.uploadNewFileFromReader(files, uploadEndpoint, source, onUploadProgress);
			this.#setUploaded(data);
			return data;
		} catch (e) {
			console.error(e.message);
			this.#hideLoaders();
		}
	}

	replaceCrop(key, dataUrl) {
		if (this.#enableAutoCrop) this.stopTracking(key);
		this.#crops[key].crop.reset().replace(dataUrl);
		if (this.#enableAutoCrop && this.#isTrackingAvailable()) {
			this.setupAutoCrop(key);
		}
	}

	replaceCrops(dataUrl) {
		if (!dataUrl) {
			this.#hideLoaders();
			return;
		}

		for (const key in this.#crops) {
			this.replaceCrop(key, dataUrl);
		}
		this.#cropUpdated = true;
		this.confirmCrop();
	}

	resetCrops() {
		for (const key in this.#crops) {
			this.#crops[key].crop.reset();
		}
		this.#cropUpdated = true;
		this.confirmCrop();
	}

	deleteCrops() {
		for (const key in this.#crops) {
			this.#crops[key].crop.destroy();
		}
		$('#crop_original').val(null);
		$('#crop_config').val('{}');
		$('#crop-editor-placeholders-container').show();
		$('#crop-editor-placeholders-label').hide();
		$('#crop-editor-crops-container').hide();
		this.#cropUpdated = true;
		// this.confirmCrop();
		$('#crop_updated').val('true');
		$('#pageForm').trigger('change');
		$('.only-if-original').hide();
	}

	getCroppedCanvas() {
		const croppedCanvas = {};

		for (const key in this.#crops) {
			const cropInstance = this.#crops[key].crop;
			if (!cropInstance) continue;

			const imageData = cropInstance.getImageData();
			const containerData = cropInstance.getContainerData();
			const canvasData = cropInstance.getCanvasData();
			croppedCanvas[key] = {
				cropper: {
					...cropInstance.getData(),
					canvasRatio: containerData.width > 0 ? imageData.width / containerData.width : 1,
					canvasLeftRatio: containerData.width > 0 ? canvasData.left / containerData.width : 0,
					canvasTopRatio: containerData.height > 0 ? canvasData.top / containerData.height : 0,
				},
			};
		}

		return croppedCanvas;
	}
}

if (typeof document !== 'undefined') {
	ResourceUtils.initGlobalDropzoneInterceptor();
}
